import * as ko from "knockout";
import {IMessageData, IPromiseRequest} from "../../index";
import {MAX_RESULTS_PER_PAGE} from "../../constants";
import pubsub from "../../pubsub";
import {createErrorMessage, getAnyValue, getNestedObjectProperty} from "../../util/misc";
import {ISelectBaseParams} from "./index";
import {global} from "../../appContext";
import {getSearchParamsFn, TSearchParams} from "../../util/request";
import {SelectAbstract} from "./SelectAbstract";
import {ERROR_MESSAGE, REQUEST_MESSAGE} from "../../constants/Language";
import baseHtml from './base.html?raw';

export let template: string = baseHtml;

export class viewModel<T> extends SelectAbstract<T> {

    protected _data: KnockoutObservableArray<T> = ko.observableArray<T>([]);
    protected isRequesting: KnockoutObservable<boolean> = ko.observable(false);
    protected messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected req: IPromiseRequest | undefined;
    private initialItem: T | undefined;
    private interface: any;

    constructor(protected override params: ISelectBaseParams<T>) {
        super(params);

        if ('interface' in params) {
            this.interface = params.interface;
            this.interface.reset = (obj: T) => {
                this.selected(this.getInitialItem(this.data(), obj));
            };
        }

        let orgChangeThrottle: number;

        this.kosub.subscribe(global.login.selectedOrganizationId, () => {
            clearTimeout(orgChangeThrottle);
            orgChangeThrottle = setTimeout(() => {
                this._data([]);
                this.request();
            }, 50)
        })

        this.kosub.subscribe(this.data, d => {
            // Empty options (loading / remount) must not wipe the bound selected value.
            if (d.length && !this.getInitialItem(d)) {
                this.selected(this.options.defaultvalue);
            }
        })

        // todo refactor to this.interface/params.interface
        if ('eventCreated' in params) {
            pubsub.on(this, params.eventCreated, (e: JQuery.Event, obj: T) => {
                this.isRequesting(true);
                this.selected(obj);
                if ('value' in params && params.key) {
                    this.value = obj[params.key];
                }
                this.request();
            });
        }

        // todo refactor to this.interface/params.interface
        if ('eventReset' in params) {
            this.initialItem = this.selected();
            pubsub.on(this, params.eventReset, (e: JQuery.Event, value: T[keyof T]) => {
                if (value && this.params.key) {
                    const item = this._data().find(o => this.getValueFromPath(o, this.params.key!) == value);
                    this.initialItem = item;
                }
                this.selected(this.initialItem);
            });
        }

        if ('data' in this.params && this.params.data != null) {
            const initialData = getAnyValue(this.params.data!);
            if (Array.isArray(initialData)) {
                this.onSuccess(initialData);
            }
        } else {
            this.req = this.getApiRequest();
            this.request();
        }
    }

    protected getApiRequest(): IPromiseRequest | undefined {
        const api = this.params.api;
        if (!api) {
            return undefined;
        }
        if (typeof api == 'string') {
            return global.getPromiseRequest(api);
        }

        if ('promiseRequest' in api) {
            return api.promiseRequest;
        }

        return global.getPromiseRequest(api.name);
    }

    protected getApiParams(): TSearchParams | undefined {
        const api = this.params.api;
        if (!api || typeof api == 'string') {
            return {} as TSearchParams;
        }

        if (api.isSearch) {
            return getSearchParamsFn(api.name)(api.params ?? {}, {resultsprpage: MAX_RESULTS_PER_PAGE})
        }

        return api.params;
    }

    protected request() {
        if (!this.req) {
            return;
        }
        this.messageData({message: REQUEST_MESSAGE, type: "info"});
        this.isRequesting(true);
        this.req.abort();
        const params = this.getApiParams();
        if (!params) {
            return;
        }
        this.req.request(params)
            .then(this.onSuccess.bind(this))
            .catch(this.onError.bind(this));
    }

    protected onSuccess(data: unknown[]) {
        if ('results' in this.params && this.params.results) {
            this.params.results(data as T[]);
        }

        let d = data as T[];

        if ('map' in this.params && this.params.map) {
            d = d.map(obj => getNestedObjectProperty(obj, this.params.map!));
        }

        this._data(d);

        this.selected(this.getInitialItem(d));

        this.messageData(null);
        this.isRequesting(false);
    }

    protected onError(reason: string) {
        this.messageData(createErrorMessage(reason, `${ERROR_MESSAGE} Klarte ikke å laste data.`));
    }

    protected override data = ko.pureComputed((): T[] => {
        if (!this._data() || !this._data().length) {
            return [];
        }

        let d: T[] = this._data();

        d = this.getFiltered(d);

        if ('exclude' in this.params && this.params.key) {
            const exclude = (this.params.exclude ? getAnyValue(this.params.exclude) : []) as T[];
            const key = this.params.key;
            d = d.filter(obj => {
                if (obj[key] == this.selected()?.[key]) {
                    return true;
                }
                return !exclude.find(e => e?.[key] == obj?.[key]);
            });
        }

        d = this._sort(d);

        if (!this.asc) {
            d = d.reverse();
        }

        return d;
    });

    override dispose() {
        super.dispose();
        this.req?.abort();
    }

}
